// Coupon management redesign (Phase 2 PR 4, 2026-05-15):
// /system/coupon-templates CRUD client.
// api 側 implementation: omikuji-api Phase 1 PR 1-3 (= release 1d70ab1 本番反映済)
// Spec: design/coupon-management-redesign-spec.md §API 契約
//
// auth: ADMIN (全件) / BUSINESS (自分が sponsor の template のみ)。
//
// 本 PR で扱う endpoint:
//   GET    /system/coupon-templates           ?landmark_id=&fortune=
//   GET    /system/coupon-templates/:id       (stats 同梱)
//   POST   /system/coupon-templates
//   PUT    /system/coupon-templates/:id
//   DELETE /system/coupon-templates/:id
//
// Phase 2 PR 5 で batch codes / Phase 2 PR 7 で csv export を追加予定。

import ApiService from './ApiService';

export default class CouponTemplateApi extends ApiService {
    list(payload?: { landmark_id?: number | string }) {
        return this.get('coupon-templates', payload);
    }

    getById(id: string | number) {
        return this.get(`coupon-templates/${id}`);
    }

    create(payload: any) {
        return this.post('coupon-templates', payload);
    }

    update(payload: { id: string | number, data: any }) {
        const { id, data } = payload;
        return this.put(`coupon-templates/${id}`, data);
    }

    remove(id: string | number) {
        return this.delete(`coupon-templates/${id}`);
    }

    // 民泊 PoC Step 3 (2026-09-26): 「翻訳を作り直す」(日本語 → en / zh-TW / ko を AI で再生成)。
    retranslate(id: string | number) {
        return this.post(`coupon-templates/${id}/retranslate`);
    }

    // POST /system/coupon-templates/:id/codes
    // Phase 2 PR 5 (2026-05-15): batch 入稿 (CSV or 直接入力)。
    // body: { codes: string[], default_valid_to?: string|null, on_duplicate?: 'skip'|'reject'|'error' }
    addCodes(payload: {
        id: string | number,
        data: { codes: string[], default_valid_to?: string | null, on_duplicate?: 'skip' | 'reject' | 'error' }
    }) {
        const { id, data } = payload;
        return this.post(`coupon-templates/${id}/codes`, data);
    }

    // GET /system/coupon-templates/:id/codes?state=&limit=&offset=
    // Phase 2 PR 5 (2026-05-15): code 一覧。本 PR は入稿 UI が中心、一覧 table は PR 6 で実装。
    listCodes(payload: {
        id: string | number,
        params?: { state?: string, limit?: number, offset?: number }
    }) {
        const { id, params } = payload;
        return this.get(`coupon-templates/${id}/codes`, params ?? {});
    }

    // GET /system/coupon-templates/:id/codes/export
    // Phase 2 PR 7 (2026-05-15): CSV エクスポート (= POS マスタ用 / 使用済集計用)。
    // response は CSV file body そのもの。本 method は ApiService 経由ではなく、axios で直接叩く
    // (= responseType: 'blob' を要するため CouponBatchCodeListSheet で fetch する)。
    // URL を返すだけで、実 download はコンポーネント側で window.open / a.click で行う。
    exportCodesUrl(id: string | number): string {
        // axios baseURL は REACT_APP_API_URL (= https://api.omikuji.jp/rest/api/v1/system) のため
        // 直接 URL を組んで download 用 anchor に渡す。Authorization header が必要なため
        // download 用は axios call + Blob 化で対応 (= 別関数 exportCodesAsBlob)。
        return `coupon-templates/${id}/codes/export`;
    }

    exportCodesAsBlob(id: string | number) {
        return this.get(this.exportCodesUrl(id), undefined, { responseType: 'blob' });
    }

    // POST /system/file
    // クーポン画像 (2026-07-02): クーポンテンプレートに設定する画像のアップロード。
    // ItemApi.uploadImage と同型 (= FileController@uploadFile、response.data.path が配信 URL)。
    // Spec: design/coupon-image-feature-spec-20260702.md
    uploadImage(payload: { file: File }) {
        return this.post('file', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
}
